import { forwardRef, useMemo, useState, useCallback, useEffect, useRef } from "react";
import { VariableSizeList as List } from "react-window";
import { useTranslation } from "react-i18next";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";

import { useFilterState } from "../../customHook/useFilterState";
import { primitiveToString, toSafeString } from "../../helpers";

import "./BaseAutocomplete.sass";

const ITEM_SIZE = 36;
const MAX_VISIBLE_ITEMS = 8;
const SEARCH_DEBOUNCE_MS = 300;
const FOCUS_DELAY_MS = 10;
const SCROLL_SETUP_DELAY_MS = 100;


const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
  const { children, onScroll, extraContent, selectAllState, onSelectAll, multiple, showSelectAll, ...other } = props;

  const selectAllItem = showSelectAll && multiple ? (
    <div key="select-all" onClick={onSelectAll} className="select_all_live" style={{ height: ITEM_SIZE }}>
      <Checkbox checked={selectAllState === "all"} indeterminate={selectAllState === "some"} size="small" style={{ marginRight: 8, padding: 0 }} />
      <span>All</span>
    </div>
  ) : null;

  const dividerItem = showSelectAll && multiple ? <Divider data-item-type="divider" /> : null;

  const extraContentItem = extraContent ? (
    <div key="extra-content" className="extra_content_all_live" style={{ height: ITEM_SIZE }}>
      {extraContent}
    </div>
  ) : null;

  const itemData = useMemo(() => [
    ...(selectAllItem ? [selectAllItem] : []),
    ...(dividerItem ? [dividerItem] : []),
    ...(extraContentItem ? [extraContentItem] : []),
    ...children,
  ], [selectAllItem, dividerItem, extraContentItem, children]);

  const itemCount = itemData.length;
  const maxHeight = MAX_VISIBLE_ITEMS * ITEM_SIZE;

  const getItemSize = useCallback((index) => {
    const item = itemData[index];
    if (item?.props?.["data-item-type"] === "divider") {
      return 1;
    }
    return ITEM_SIZE;
  }, [itemData]);
  
  const Row = useCallback(({ index, style, data }) => (
    <div style={style}>{data[index]}</div>
  ), []);

  return (
    <div ref={ref}>
      <div {...other} data-testid="virtual-list-container">
        <List
          height={Math.min(maxHeight, itemCount * ITEM_SIZE)}
          width="100%"
          itemCount={itemCount}
          itemSize={getItemSize}
          itemData={itemData}
          onScroll={onScroll}
          overscanCount={5}
          useIsScrolling
        >
          {Row}
        </List>
      </div>
    </div>
  );
});

export const BaseLiveLoadAutocomplete = ({
  value,
  onBlur,
  header,
  initialResponse = null,
  isLoading,
  getOptionLabel,
  getOptionValue,
  placeholder,
  renderOption,
  isEditing = false,
  handleFilterSet,
  multiple = false,
  limitTags = 2,
  onLoadPage,
  onSearchOptions,
  selectedOption,
  onSelectOption,
  defaultPageSize = 50,
  resetFilter,
  setIsEditing
}) => {
  const { t } = useTranslation();
  const { filterValue, setFilter, handleBlur } = useFilterState(value, onBlur);

  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [paginationDetails, setPaginationDetails] = useState({ onPage: 1, pageSize: defaultPageSize, totalPages: null, totalRecords: null });
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [shouldPreserveFocus, setShouldPreserveFocus] = useState(false);
  const [optionsMap, setOptionsMap] = useState(new Map());
  const [searchResults, setSearchResults] = useState([]);
  const [scrollState, setScrollState] = useState({ isScrolling: false, lastScrollTime: 0 });
  const [hasBeenCleared, setHasBeenCleared] = useState(false);

  const searchTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const lastInitialResponseRef = useRef(null);
  const inputRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const lastSearchTermRef = useRef("");

  const isTariffField = header === "tariffId" || header === "tariffNumber";

  const stableGetOptionValue = useCallback((option) => {
    if (option === null || option === undefined) {
      return "";
    }

    if (typeof getOptionValue === "function") {
      const derivedValue = getOptionValue(option);
      if (derivedValue !== undefined && derivedValue !== null && derivedValue !== "") {
        return toSafeString(derivedValue);
      }
    }

    const primitive = primitiveToString(option);
    if (primitive !== null) {
      return primitive;
    }

    const fallbackValue = option?.value ?? option?.id ?? option;
    return toSafeString(fallbackValue);
  }, [getOptionValue]);

  const stableGetOptionLabel = useCallback((option) => {
    if (option === null || option === undefined) {
      return "";
    }

    if (typeof getOptionLabel === "function") {
      const derivedLabel = getOptionLabel(option);
      if (derivedLabel !== undefined && derivedLabel !== null && derivedLabel !== "") {
        return toSafeString(derivedLabel);
      }
    }

    const primitive = primitiveToString(option);
    if (primitive !== null) {
      return primitive;
    }

    const fallbackLabel = option?.label ?? option?.name ?? option?.description ?? option?.value ?? option?.id ?? option;
    return toSafeString(fallbackLabel);
  }, [getOptionLabel]);

  const renderDefaultOption = useCallback((props, option) => {
    const optionLabel = stableGetOptionLabel(option);

    return (
      <li {...props}>
        <Box className="autocomplete-option-label" title={optionLabel}>
          {optionLabel}
        </Box>
      </li>
    );
  }, [stableGetOptionLabel]);

  const isSearchResponse = useCallback((response) => {
    return response?.isSearch || (response?.searchTerms?.length ?? 0) > 0;
  }, []);

  const parseResponseData = useCallback((response) => {
    if (response.data && Array.isArray(response.data)) {
      return {
        data: response.data,
        pagination: { onPage: response.onPage || 1, pageSize: response.pageSize || defaultPageSize, totalPages: response.totalPages, totalRecords: response.totalRecords },
        isSearch: isSearchResponse(response),
      };
    }

    if (Array.isArray(response)) {
      return {
        data: response,
        pagination: { onPage: 1, pageSize: defaultPageSize, totalPages: null, totalRecords: response.length },
        isSearch: false,
      };
    }

    return null;
  }, [defaultPageSize, isSearchResponse]);

  const updateOptionsMap = useCallback((data) => {
    if (!data || data.length === 0) return;
    
    setOptionsMap((prevMap) => {
      let hasChanges = false;
      const newMap = new Map(prevMap);
      
      data.forEach((option) => {
        const key = stableGetOptionValue(option);
        if (!newMap.has(key)) {
          newMap.set(key, option);
          hasChanges = true;
        }
      });
      
      return hasChanges ? newMap : prevMap;
    });
  }, [stableGetOptionValue]);

  useEffect(() => {
    if (!initialResponse) return;
    
    const responseKey = JSON.stringify({
      data: initialResponse.data?.length || 0,
      page: initialResponse.onPage,
      isLoading: initialResponse.isLoading
    });
    
    if (lastInitialResponseRef.current === responseKey) {
      return;
    }

    lastInitialResponseRef.current = responseKey;

    const parsedData = parseResponseData(initialResponse);
    if (!parsedData || !Array.isArray(parsedData.data) || parsedData.data.length === 0) {
      setIsLoadingMore(false);
      return;
    }

    const { data, pagination, isSearch } = parsedData;
    const wasInputFocused = document.activeElement?.tagName === "INPUT" && 
                            document.activeElement?.getAttribute("aria-autocomplete") === "list";

    const hasManualInput = typeof inputValue === "string" && inputValue.trim().length > 0;

    if (isSearch || hasManualInput) {
      setSearchResults(data);
      setSearchMode(true);
      setIsSearching(false);
      setIsLoadingMore(false);
      if (wasInputFocused) setShouldPreserveFocus(true);
    } else {
      updateOptionsMap(data);
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
      }
      
      const nextPage = pagination.totalPages !== null 
        ? pagination.onPage < pagination.totalPages 
        : data.length === pagination.pageSize;

      setPaginationDetails(pagination);
      setHasNextPage(isTariffField ? false : nextPage);
      setIsLoadingMore(false);
      if (wasInputFocused) setShouldPreserveFocus(true);
    }
  }, [initialResponse, parseResponseData, updateOptionsMap, inputValue, isTariffField]);

  useEffect(() => {
    if (isTariffField) {
      setHasNextPage(false);
    }
  }, [isTariffField]);

  const currentOptions = useMemo(() => {
    const resolvedBase = searchMode
      ? searchResults.flat()
      : isEditing
        ? (Array.isArray(initialResponse?.data) ? initialResponse.data : [])
        : Array.from(optionsMap.values()).flat();

    if (isEditing) {
      return resolvedBase;
    }

    let baseOptions = resolvedBase;

    const currentFilterValues = multiple 
      ? (Array.isArray(filterValue) ? filterValue : filterValue ? [filterValue] : [])
      : (filterValue ? [filterValue] : []);

    if (selectedOption && !hasBeenCleared) {
      const optionsToCheck = Array.isArray(selectedOption) ? selectedOption : [selectedOption];
      
      optionsToCheck.forEach((option) => {
        const optionValue = stableGetOptionValue(option);
        const isValidOption = option && optionValue !== null && optionValue !== undefined && optionValue !== "";
        
        if (isValidOption) {
          const exists = baseOptions.some((opt) => stableGetOptionValue(opt) === optionValue);
          if (!exists) {
            baseOptions = [option, ...baseOptions];
          }
        }
      });
    }
    
    if (currentFilterValues.length > 0 && !hasBeenCleared) {
      currentFilterValues.forEach((rawFilterVal) => {
        if (rawFilterVal === undefined || rawFilterVal === null || rawFilterVal === "") {
          return;
        }

        const normalizedFilterVal = stableGetOptionValue(rawFilterVal);
        const existingIndex = baseOptions.findIndex((opt) => stableGetOptionValue(opt) === normalizedFilterVal);

        if (optionsMap.has(normalizedFilterVal)) {
          const mappedOption = optionsMap.get(normalizedFilterVal);

          if (existingIndex === -1) {
            baseOptions = [mappedOption, ...baseOptions];
          } else if (baseOptions[existingIndex] !== mappedOption) {
            const nextOptions = [...baseOptions];
            nextOptions[existingIndex] = mappedOption;
            baseOptions = nextOptions;
          }

          return;
        }

        if (existingIndex === -1) {
          const fallbackValue = normalizedFilterVal ?? String(rawFilterVal);
          const fallbackOption = typeof rawFilterVal === "object" && rawFilterVal !== null
            ? rawFilterVal
            : {
                value: fallbackValue,
                label: fallbackValue,
                name: fallbackValue,
                __isFallback: true,
              };

          baseOptions = [fallbackOption, ...baseOptions];
        }
      });
    }

    const filtered = baseOptions.filter((option) => {
      const optionValue = stableGetOptionValue(option);
      return option && optionValue !== null && optionValue !== undefined && optionValue !== "";
    });

    return filtered;
  }, [initialResponse, isEditing, optionsMap, searchResults, searchMode, selectedOption, stableGetOptionValue, hasBeenCleared, filterValue, multiple]);
  
  useEffect(() => {
    if (shouldPreserveFocus && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
        setShouldPreserveFocus(false);
      }, FOCUS_DELAY_MS);

      return () => clearTimeout(timer);
    }
  }, [shouldPreserveFocus]);
  
  const selectedOptions = useMemo(() => {
    if (multiple) {
      const values = Array.isArray(filterValue) ? filterValue : filterValue ? [filterValue] : [];
      const filtered = currentOptions.filter((option) => values.includes(stableGetOptionValue(option)));
      return filtered;
    }

    const found = currentOptions.find((option) => stableGetOptionValue(option) === filterValue) || null;
    return found;
  }, [currentOptions, filterValue, stableGetOptionValue, multiple]);


  const selectAllState = useMemo(() => {
    if (!multiple || !currentOptions.length) return "none";

    const selectedValues = Array.isArray(filterValue) ? filterValue : [];
    const currentOptionValues = currentOptions.map(stableGetOptionValue);
    const selectedCount = currentOptionValues.filter((val) => selectedValues.includes(val)).length;

    if (selectedCount === 0) return "none";
    if (selectedCount === currentOptions.length) return "all";
    return "some";
  }, [multiple, currentOptions, filterValue, stableGetOptionValue, selectedOption]);

  const handleSelectAll = useCallback(() => {
    if (!multiple) return;

    onSelectOption(currentOptions);
    const newValues = selectAllState === "all" ? [] : currentOptions.map(stableGetOptionValue);

    setFilter(newValues);
    if (isEditing) onBlur(newValues);
    if (handleFilterSet) handleFilterSet(newValues);
  }, [multiple, currentOptions, stableGetOptionValue, selectAllState, setFilter, isEditing, onBlur, handleFilterSet, onSelectOption]);

  const loadNextPage = useCallback(async () => {
    if (isTariffField || !hasNextPage || isLoadingMore || searchMode || !onLoadPage) return;
    
    const now = Date.now();
    if (now - scrollState.lastScrollTime < 500) return;
    
    setScrollState({ isScrolling: true, lastScrollTime: now });
    setIsLoadingMore(true);
    
    try {
      if (paginationDetails.totalPages === null || paginationDetails.onPage < paginationDetails.totalPages) {
        await onLoadPage({ 
          searchTerms: [], 
          onPage: paginationDetails.onPage + 1, 
          pageSize: paginationDetails.pageSize 
        });
      }
    } catch (error) {
      console.error("Error loading next page:", error);
      setHasNextPage(false);
      setIsLoadingMore(false);
    } finally {
      setTimeout(() => setScrollState((prev) => ({ ...prev, isScrolling: false })), 100);
    }
  }, [isTariffField, hasNextPage, isLoadingMore, searchMode, paginationDetails, onLoadPage, scrollState.lastScrollTime]);

  useEffect(() => {
    if (!isOpen) return;

    let scrollTimeout = null;
    const timer = setTimeout(() => {
      const popperElement = document.querySelector('[role="presentation"] .MuiAutocomplete-listbox');
      return () => {};
    }, SCROLL_SETUP_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isOpen, hasNextPage, isLoadingMore, searchMode, loadNextPage, scrollState.isScrolling]);

  useEffect(() => {
    if (!multiple && selectedOptions && !searchMode && !isOpen) {
      setInputValue(stableGetOptionLabel(selectedOptions));
    }
  }, [selectedOptions, multiple, searchMode, isOpen, stableGetOptionLabel]);

  useEffect(() => {
    const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
    if (isEmpty) {
      setInputValue("");
      setSearchResults([]);
      setSearchMode(false);
      lastSearchTermRef.current = "";
      if (onSelectOption) {
        onSelectOption(multiple ? [] : null);
      }
    }
  }, [value, multiple, onSelectOption]);

  const handleScroll = useCallback((scrollInfo) => {
    if (scrollInfo.scrollUpdateWasRequested || scrollInfo.scrollDirection !== "forward") return;
    if (scrollState.isScrolling) return;

    const { scrollOffset } = scrollInfo;
    const totalItems = currentOptions.length;
    const visibleItemsCount = Math.min(MAX_VISIBLE_ITEMS, totalItems);
    const maxScrollOffset = Math.max(0, (totalItems - visibleItemsCount) * ITEM_SIZE);
    const distanceFromBottom = maxScrollOffset - scrollOffset;
    const threshold = ITEM_SIZE * 2;
    const hasScrolledSignificantly = scrollOffset > ITEM_SIZE;
    const isNearBottom = distanceFromBottom <= threshold;

    if (hasScrolledSignificantly && isNearBottom && hasNextPage && !isLoadingMore && !searchMode) {
      loadNextPage();
    }
  }, [hasNextPage, isLoadingMore, searchMode, loadNextPage, currentOptions.length, scrollState.isScrolling]);

  const performSearch = useCallback(async (searchValue) => {
    const normalizedSearch = toSafeString(searchValue);

    if (!normalizedSearch.trim()) {
      setIsSearching(false);
      setSearchMode(false);
      setSearchResults([]);
      if (typeof resetFilter === "function") {
        resetFilter();
      }
      return;
    }

    setIsSearching(true);
    setSearchMode(true);
    lastSearchTermRef.current = normalizedSearch;

    try {
      const searchTerms = normalizedSearch.split(";").map((term) => term.trim()).filter((term) => term.length > 0);

      if (onSearchOptions && searchTerms.length > 0) {
        await onSearchOptions(searchTerms);
      }
      setIsSearching(false);
    } catch (error) {
      console.error("Error searching options:", error);
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [onSearchOptions, resetFilter]);

  const handleClearSelection = useCallback(() => {
    setHasBeenCleared(false);
    lastSearchTermRef.current = "";
    setInputValue("");
    setSearchMode(false);
    setSearchResults([]);
    if (typeof resetFilter === "function") {
      resetFilter();
    }
  }, [resetFilter]);

  const handleFreeTextSelection = useCallback(async (value) => {
    setInputValue(value);
    if (onSearchOptions) {
      await onSearchOptions([value]);
    }
    setFilter(value);
    if (isEditing) onBlur(value);
    handleFilterSet?.(value);
  }, [onSearchOptions, setFilter, isEditing, onBlur, handleFilterSet]);

  const handleOptionSelect = useCallback(async (option) => {
    if (!option || !onSelectOption) return;

    try {
      await onSelectOption(option);
    } catch (error) {
      console.error("Error on option select:", error);
    }
  }, [onSelectOption]);

  const clearSearchState = useCallback(() => {
    setSearchMode(false);
    setSearchResults([]);
    setIsSearching(false);
    lastSearchTermRef.current = "";

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  const commitMultipleSelection = useCallback((newValue) => {
    const selectedValues = newValue ? newValue.map(stableGetOptionValue) : [];
    setFilter(selectedValues);
    if (isEditing) onBlur(selectedValues);
    handleFilterSet?.(selectedValues);

    setHasBeenCleared(false);
    setInputValue(searchMode ? lastSearchTermRef.current : "");

    if (searchMode) {
      setShouldPreserveFocus(true);
    } else {
      setSearchMode(false);
      setSearchResults([]);
    }
  }, [stableGetOptionValue, setFilter, isEditing, onBlur, handleFilterSet, searchMode, setShouldPreserveFocus]);

  const commitSingleSelection = useCallback((newValue) => {
    const selectedValue = newValue ? stableGetOptionValue(newValue) : "";
    setFilter(selectedValue);
    if (isEditing) onBlur(selectedValue);
    handleFilterSet?.(selectedValue);

    if (newValue) {
      setInputValue(stableGetOptionLabel(newValue));
      setHasBeenCleared(false);
      setSearchMode(false);
      setSearchResults([]);
    } else {
      setInputValue("");
    }
  }, [stableGetOptionValue, stableGetOptionLabel, setFilter, isEditing, onBlur, handleFilterSet]);

  const handleChange = useCallback(async (event, newValue, reason) => {
    if (reason === "clear") {
      handleClearSelection();
      return;
    }

    if (typeof newValue === "string") {
      await handleFreeTextSelection(newValue);
      return;
    }

    await handleOptionSelect(newValue);

    if (multiple) {
      commitMultipleSelection(newValue);
      return;
    }

    commitSingleSelection(newValue);
  }, [handleClearSelection, handleFreeTextSelection, handleOptionSelect, multiple, commitMultipleSelection, commitSingleSelection]);

  const handleInputChange = useCallback((event, newInputValue, reason) => {
    const normalizedInput = toSafeString(newInputValue);

    if (reason === "input") {
      setInputValue(normalizedInput);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (normalizedInput.trim()) {
        searchTimeoutRef.current = setTimeout(() => {
          performSearch(normalizedInput);
        }, SEARCH_DEBOUNCE_MS);
      } else {
        setSearchMode(false);
        setSearchResults([]);
      }
    }

    if (reason === "reset") {
      if (!multiple && selectedOptions && !normalizedInput) {
        setInputValue(stableGetOptionLabel(selectedOptions));
      }
    }

    if (reason === "clear") {
      setInputValue("");
      lastSearchTermRef.current = "";
      setSearchMode(false);
      setSearchResults([]);
      if (typeof resetFilter === "function") {
        resetFilter();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    }
  }, [performSearch, multiple, selectedOptions, stableGetOptionLabel, searchMode]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);

    if (isEditing) {
      clearSearchState();

      if (typeof resetFilter === "function") {
        resetFilter();
      }

      if (!multiple && selectedOptions) {
        setInputValue(stableGetOptionLabel(selectedOptions));
      } else if (multiple) {
        setInputValue("");
      }
    }
  }, [isEditing, clearSearchState, resetFilter, multiple, selectedOptions, stableGetOptionLabel]);

  const handleClose = useCallback(() => {
    setIsOpen(false);

    if (multiple) {
      setInputValue(searchMode ? (inputValue || lastSearchTermRef.current) : "");
    } else if (selectedOptions) {
      setInputValue(stableGetOptionLabel(selectedOptions));
    } else {
      setInputValue("");
    }

    if (!searchMode) {
      setSearchResults([]);
    }
    setShouldPreserveFocus(false);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (setIsEditing && blurTimeoutRef.current === null) {
      blurTimeoutRef.current = setTimeout(() => {
        if (setIsEditing && inputValue !== "") {
          setIsEditing(false);
        }
        blurTimeoutRef.current = null;
      }, 3000);
    }
  }, [multiple, selectedOptions, stableGetOptionLabel, inputValue, setIsEditing, searchMode]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleChipDelete = useCallback((currentSelection, chipIndex) => {
    const newSelected = currentSelection.filter((_, i) => i !== chipIndex);
    const selectedValues = newSelected.map(stableGetOptionValue);

    if (onSelectOption) {
      onSelectOption(newSelected);
    }

    setFilter(selectedValues);

    if (isEditing && typeof onBlur === "function") {
      onBlur(selectedValues);
    }

    handleFilterSet?.(selectedValues);
  }, [handleFilterSet, isEditing, onBlur, onSelectOption, setFilter, stableGetOptionValue]);

  const isBusy = isLoading || isSearching || isLoadingMore;
  const shouldShowLoading = (isOpen || isEditing) && isBusy;

  const extraContent = useMemo(() => {
    if (isLoadingMore && !isSearching) {
      return (
        <>
          Loading more options...
          {paginationDetails.totalPages && <> ({paginationDetails.onPage + 1}/{paginationDetails.totalPages})</>}
        </>
      );
    }

    if (searchMode && !isSearching && searchResults.length === 0 && inputValue) {
      return <>No search results found for "{inputValue}"</>;
    }

    if (!searchMode && paginationDetails.totalRecords !== null) {
      return <>{optionsMap.size} of {paginationDetails.totalRecords} items loaded</>;
    }

    return null;
  }, [isLoadingMore, isSearching, searchMode, searchResults, inputValue, paginationDetails, optionsMap.size]);

  const EnhancedListboxComponent = useMemo(() => forwardRef(function EnhancedListboxComponent(props, ref) {
    return (
      <ListboxComponent
        {...props}
        ref={ref}
        onScroll={handleScroll}
        extraContent={extraContent}
        selectAllState={selectAllState}
        onSelectAll={handleSelectAll}
        multiple={multiple}
        showSelectAll={multiple && currentOptions.length > 0}
      />
    );
  }), [handleScroll, extraContent, selectAllState, handleSelectAll, multiple, currentOptions.length]);

  const noOptionsText = useMemo(() => (
    t("no_data_to_display")
  ), []);

  const loadingText = useMemo(() => (
    t("loading_records")
  ), []);

  return (
    <div className="auto_block">
      <Box>
        {multiple && selectedOptions?.length > 0 && (
          <Box className="selected-chips-container">
            <Box className="scroll-container">
              {selectedOptions.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={stableGetOptionLabel(option)}
                  size="small"
                  onDelete={() => handleChipDelete(selectedOptions, index)}
                  key={stableGetOptionValue(option)}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Autocomplete
        size="small"
        fullWidth
        multiple={multiple}
        limitTags={limitTags}
        disableCloseOnSelect={multiple}
        data-testid={`${header}-${value}`}
        autoFocus={isEditing}
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        value={selectedOptions}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onBlur={!isEditing ? handleBlur : undefined}
        options={currentOptions}
        getOptionLabel={stableGetOptionLabel}
        isOptionEqualToValue={(option, values) => stableGetOptionValue(option) === stableGetOptionValue(values)}
        loading={shouldShowLoading}
        loadingText={loadingText}
        ListboxComponent={EnhancedListboxComponent}
        title=""
        filterOptions={(options) => options}
        filterSelectedOptions
        renderTags={() => null}
        className="com-autocomplete-container"
        freeSolo={!isEditing}
        noOptionsText={noOptionsText}
        renderInput={(params) => (
          <TextField
            {...params}
            ref={inputRef}
            label={isEditing ? "" : t(header)}
            placeholder={placeholder || "Type to search (separate with ';' for multiple terms)"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {shouldShowLoading && <CircularProgress color="inherit" size={18} sx={{ mt: -3 }} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={renderOption || renderDefaultOption}
      />
    </div>
  );
};
